import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Alert,
  ButtonGroup,
  Badge,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchFAQsAdmin,
  fetchFAQById,
  toggleFAQ,
  eliminarFAQ,
  limpiarSeleccionada,
} from "../../redux/faqSlice";
import FAQCanvasEditor from "../../components/faq/FAQCanvasEditor";
import PapeleraFAQModal from "../../components/faq/PapeleraFAQModal";

export default function FAQAdminPage() {
  const dispatch = useDispatch();
  const { admin, seleccionada, loading, error } = useSelector((s) => s.faq);

  const [showEditor, setShowEditor] = useState(false);
  const [cargandoEdicion, setCargandoEdicion] = useState(false);
  const [showPapelera, setShowPapelera] = useState(false);

  useEffect(() => {
    dispatch(fetchFAQsAdmin());
  }, [dispatch]);

  const abrirCrear = () => {
    dispatch(limpiarSeleccionada());
    setShowEditor(true);
  };

  const abrirEditar = async (id) => {
    setCargandoEdicion(true);
    try {
      await dispatch(fetchFAQById(id)).unwrap();
      setShowEditor(true);
    } catch (err) {
      Swal.fire("Error", "No se pudo cargar la FAQ para editar.", "error");
    } finally {
      setCargandoEdicion(false);
    }
  };

  const cerrarEditor = () => {
    setShowEditor(false);
    dispatch(limpiarSeleccionada());
  };

  const trasGuardar = () => {
    setShowEditor(false);
    dispatch(limpiarSeleccionada());
    dispatch(fetchFAQsAdmin()); // refresca la tabla
  };

  const onToggle = async (faq) => {
    const desactivando = faq.estado_activo; // si está activa, la vamos a desactivar
    const result = await Swal.fire({
      title: desactivando ? "¿Deshabilitar esta FAQ?" : "¿Activar esta FAQ?",
      text: desactivando
        ? "Dejará de mostrarse a los socios en la página pública."
        : "Volverá a mostrarse a los socios.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: desactivando ? "#d0021b" : "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: desactivando ? "Sí, deshabilitar" : "Sí, activar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await dispatch(toggleFAQ(faq.id)).unwrap();
      Swal.fire({
        icon: "success",
        title: desactivando ? "FAQ deshabilitada" : "FAQ activada",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire("Error", "No se pudo cambiar el estado.", "error");
    }
  };

  const onEliminar = async (id) => {
    const r = await Swal.fire({
      title: "¿Enviar a la papelera?",
      text: "La FAQ dejará de mostrarse. Podrás restaurarla desde la papelera.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    try {
      await dispatch(eliminarFAQ(id)).unwrap();
      Swal.fire("Eliminada", "La FAQ fue enviada a la papelera.", "success");
    } catch (err) {
      Swal.fire("Error", "No se pudo eliminar la FAQ.", "error");
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h3 mb-0">Gestión de Preguntas Frecuentes</h1>
        <Button
          variant="success"
          onClick={abrirCrear}
          className="d-flex align-items-center"
        >
          <i className="bi bi-plus-circle me-2"></i>
          <span className="d-none d-sm-inline">Crear FAQ</span>
        </Button>
        <Button
          variant="outline-secondary"
          className="me-2 d-inline-flex align-items-center gap-1"
          onClick={() => setShowPapelera(true)}
        >
          <i className="bi bi-trash"></i>
          Papelera
        </Button>
      </div>

      {error && (
        <Alert variant="danger">
          {typeof error === "string"
            ? error
            : error?.message || "Ocurrió un error"}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando…</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="small">
            <thead className="table-dark">
              <tr>
                <th>Título</th>
                <th className="text-center">Estado</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {admin.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-muted">
                    No hay FAQ registradas
                  </td>
                </tr>
              ) : (
                admin.map((faq) => (
                  <tr key={faq.id}>
                    <td
                      className="text-truncate"
                      style={{ maxWidth: 320 }}
                      title={faq.titulo}
                    >
                      {faq.titulo}
                    </td>
                    <td className="text-center">
                      <Badge bg={faq.estado_activo ? "success" : "secondary"}>
                        {faq.estado_activo ? "Activa" : "Inactiva"}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <ButtonGroup size="sm">
                        <Button
                          variant="outline-primary"
                          onClick={() => abrirEditar(faq.id)}
                          disabled={cargandoEdicion}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"></i>
                          <span className="d-none d-lg-inline ms-1">
                            Editar
                          </span>
                        </Button>
                        <Button
                          variant={
                            faq.estado_activo
                              ? "outline-secondary"
                              : "outline-success"
                          }
                          onClick={() => onToggle(faq)}
                          title={faq.estado_activo ? "Desactivar" : "Activar"}
                        >
                          <i
                            className={`bi ${faq.estado_activo ? "bi-eye-slash" : "bi-eye"}`}
                          ></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={() => onEliminar(faq.id)}
                          title="Eliminar"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      {/* Editor en modal a pantalla completa */}
      <Modal
        show={showEditor}
        onHide={cerrarEditor}
        fullscreen
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{seleccionada ? "Editar FAQ" : "Crear FAQ"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showEditor && (
            <FAQCanvasEditor
              faq={seleccionada}
              onGuardado={trasGuardar}
              onCancelar={cerrarEditor}
            />
          )}
        </Modal.Body>
      </Modal>
      <PapeleraFAQModal
        show={showPapelera}
        onClose={() => setShowPapelera(false)}
        onCambio={() => dispatch(fetchFAQsAdmin())}
      />
    </div>
  );
}
